import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { FilterAltRounded, CachedOutlined } from "@mui/icons-material"
import {
  getBankList,
  getPaymentMethods,
  getProcessSchedule,
  getTransactionList,
  PaginationType,
  resetFilters,
  selectSortModel,
  selectTransactions,
  setFilterCount,
  setFilters,
  setFiltersFromURL,
  setPagination,
  transactionsInitialState,
} from "../../slices/transactionsSlice"
import PageHeader from "../../components/PageHeader/PageHeader"
import {
  Box,
  Button,
  Divider,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material"
import { TransactionTable } from "./TransactionTable"
import UploadPopup from "./UploadPopup"
import TransactionsFilters from "./TransactionsFilter"
import { useLocation, useNavigate } from "react-router-dom"
import queryString from "query-string"
import { constructApiRequestParams } from "../../utils/transactionHelpers"
import {
  allowedCategorisationRoles,
  allowedOnlyBankAccountsRoles,
  operators,
  processScheduleStatus,
  TransactionType,
  transactionTypeLabels,
} from "../../constants/data"
import { selectLogin } from "../../slices/loginSlice"
import { GridSortItem, GridSortModel } from "@mui/x-data-grid"
import { ROOT } from "../../constants/path"
import {
  ContentWrapper,
  ReconciliationTimeMessage,
  StyledBox,
  StyledButton,
  StyledCircle,
  StyledFilterDrawer,
  StyledFormControl,
  StyledTypeFormControl,
  TransactionsContainer,
  TransactionsFilterWrapper,
} from "./styles"
import { PageHeaderAction } from "./PageHeaderAction"
import CloseIcon from "@mui/icons-material/Close"
import TransactionCategorySelector from "./TransactionCategorySelector"
import { GridParent } from "../BankAccounts/styles"
import ScheduleReconciliationPopup from "./ScheduleReconciliationPopup"
import CustomSearchBox from "./CustomSearchBox"
import DateRangeSelector from "../../components/DateRangeSelector/DateRangeSelector"
import dayjs, { Dayjs } from "dayjs"
import { Bank } from "../../models/transactions.model"

export const Transactions = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    processScheduleData,
    transactionList,
    pageInfo,
    isLoading,
    filters,
    bankList,
    pagination,
    filterCount,
    category,
    rowSelectionModel,
    isUploading,
    isZohoSyncLoading,
    isZohoSyncSuccess,
  } = useAppSelector(selectTransactions)
  const sortModel = useAppSelector(selectSortModel)
  const { role } = useAppSelector(selectLogin)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [ScheduleDialogOpen, setScheduleDialogOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const drawerWidth = 280
  const [refreshTime, setRefreshTime] = useState<Date | null>(null)

  useEffect(() => {
    dispatch(getBankList())
    dispatch(getPaymentMethods())
    if (allowedOnlyBankAccountsRoles.includes(role)) {
      dispatch(getProcessSchedule())
    }
    // URL Parameters to Redux State
    const params = queryString.parse(location.search)

    if (Object.keys(params).length !== 0) {
      // URL has filter parameters
      if (!Array.isArray(params.page) && !Array.isArray(params.pageSize)) {
        const page = parseInt(params.page || "0")
        const pageSize = parseInt(params.pageSize || "10")
        dispatch(setPagination({ page, pageSize }))
      }
      const updatedFilters = { ...filters, ...params }
      dispatch(setFiltersFromURL(updatedFilters))
      dispatch(setFilterCount())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchTransaction = () => {
    setRefreshTime(new Date())
    dispatch(setFilterCount())
    const params = queryString.stringify({ ...filters, ...pagination })
    navigate({ search: params })
    dispatch(getTransactionList(constructApiRequestParams(filters, pagination)))
  }

  const updateBankList = (bankList: Bank[]) => {
    const updatedAccountId: string[] = []
    const bankIdsSet = new Set(filters.accountId || [])

    bankList
      .filter((bank) => bank.isDefaultSelected)
      .forEach((bank) => {
        if (!bankIdsSet.has(bank.id)) {
          bankIdsSet.add(bank.id)
          updatedAccountId.push(bank.id)
        }
      })

    dispatch(
      setFilters({
        ...filters,
        accountId: updatedAccountId,
      }),
    )
    dispatch(setFilterCount())
    dispatch(
      getTransactionList(
        constructApiRequestParams(
          { ...filters, accountId: updatedAccountId },
          pagination,
        ),
      ),
    )
  }

  useEffect(() => {
    if (
      JSON.stringify(filters) ===
        JSON.stringify(transactionsInitialState.filters) &&
      bankList.length
    )
      // fetchTransaction()
      updateBankList(bankList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, bankList])

  useEffect(() => {
    // set filters to URL for sharing purpose
    if (
      JSON.stringify(filters) !==
      JSON.stringify(transactionsInitialState.filters)
    ) {
      fetchTransaction()
      if (allowedOnlyBankAccountsRoles.includes(role)) {
        dispatch(getProcessSchedule())
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  // reset filter handler
  const resetFilterHandler = () => {
    dispatch(resetFilters())
    setFilterOpen(false)
  }

  const handleSetUploadDialogClose = () => {
    setUploadDialogOpen(false)
  }
  const handleSetScheduleDialogClose = () => {
    setScheduleDialogOpen(false)
  }

  const setPaginationModel = (page: PaginationType) => {
    dispatch(setPagination(page))
  }

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    if (!newSortModel.length) {
      dispatch(setFilters({ ...filters, sortBy: "none" }))
    } else {
      dispatch(
        setFilters({
          ...filters,
          sortBy: newSortModel[0].field,
          orderBy: newSortModel[0].sort,
        }),
      )
    }
  }

  const handleDrawerClose = () => {
    setFilterOpen(false)
  }

  const ApplyFilterHandler = () => {
    fetchTransaction()
    setFilterOpen(false)
  }

  useEffect(() => {
    if (
      JSON.stringify(filters) !==
      JSON.stringify(transactionsInitialState.filters)
    )
      fetchTransaction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.search,
    filters.dateSelector,
    filters.transactionType,
    filters.orderBy,
    filters.operator,
  ])
  useEffect(() => {
    !isUploading && fetchTransaction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading])

  useEffect(() => {
    !isZohoSyncLoading && isZohoSyncSuccess && fetchTransaction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZohoSyncSuccess])

  const handleRefresh = () => {
    setRefreshTime(new Date())
    dispatch(getTransactionList(constructApiRequestParams(filters, pagination)))
    if (allowedOnlyBankAccountsRoles.includes(role)) {
      dispatch(getProcessSchedule())
    }
  }

  const handleDateRangeSelection = (newState: {
    dateSelector: string
    startDate?: Dayjs | null
    endDate?: Dayjs | null
  }) => {
    dispatch(
      setFilters({
        ...filters,
        ...newState,
      }),
    )
  }

  return (
    <GridParent>
      <PageHeader
        title="Transactions"
        breadcrums={[{ title: "Home", path: ROOT }, { title: "Transactions" }]}
        actions={
          <PageHeaderAction
            setUploadDialogOpen={setUploadDialogOpen}
            setScheduleDialogOpen={setScheduleDialogOpen}
          />
        }
      />
      <TransactionsContainer sx={{ display: "flex", flexDirection: "row" }}>
        <ContentWrapper isOpen={filterOpen} drawerWidth={drawerWidth}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            {processScheduleData.status && (
              <div>
                {processScheduleData.scheduleStatus ===
                processScheduleStatus.PENDING ? (
                  <ReconciliationTimeMessage>
                    Reconciliation will start at {processScheduleData?.nextRun}.
                    Please upload all pending bank statements to ensure
                    inclusion. Any remaining statements may be uploaded after
                    the upcoming cycle ends.
                  </ReconciliationTimeMessage>
                ) : (
                  <ReconciliationTimeMessage>
                    Reconciliation is in progress and should complete in 15–30
                    minutes. Any new bank statements uploaded during this time
                    will be added to the queue and processed in the next
                    reconciliation cycle.
                  </ReconciliationTimeMessage>
                )}
              </div>
            )}
            <StyledBox
              justifyContent={"space-between"}
              display="flex"
              gap={2}
              alignItems={"flex-start"}
            >
              <TransactionsFilterWrapper>
                <CustomSearchBox />
                {filters.searchType === "amount" && (
                  <Box
                    gap={0}
                    display={"flex"}
                    alignItems={"center"}
                    flex={1}
                    width={"100%"}
                  >
                    <StyledFormControl size="small" sx={{ width: 90 }}>
                      <InputLabel id="select-opertator">Operator</InputLabel>
                      <Select
                        labelId="select-opertator"
                        id="selectOperator"
                        label="Operator"
                        value={filters.operator}
                        onChange={(e: SelectChangeEvent) =>
                          dispatch(
                            setFilters({
                              ...filters,
                              operator: e.target.value as string,
                            }),
                          )
                        }
                        disabled={filters.searchType !== "amount"}
                      >
                        {operators.map((operator) => (
                          <MenuItem value={operator?.value}>
                            {operator?.option}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledFormControl>
                  </Box>
                )}
                <DateRangeSelector
                  dateSelector={filters.dateSelector}
                  filterState={filters}
                  updateFilterState={handleDateRangeSelection}
                />
                <StyledTypeFormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="select-transaction-type">Type</InputLabel>
                  <Select
                    labelId="select-transaction-type"
                    id="selectTransactionType"
                    label="Type"
                    value={filters.transactionType}
                    onChange={(e: SelectChangeEvent) =>
                      dispatch(
                        setFilters({
                          ...filters,
                          transactionType: e.target.value as string,
                        }),
                      )
                    }
                  >
                    <MenuItem value="all">All</MenuItem>
                    {Object.values(TransactionType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {transactionTypeLabels[type]}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledTypeFormControl>
                <StyledButton
                  variant="outlined"
                  startIcon={<FilterAltRounded fontSize="small" />}
                  endIcon={
                    Boolean(filterCount) && (
                      <StyledCircle>{filterCount}</StyledCircle>
                    )
                  }
                  color="inherit"
                  onClick={() => setFilterOpen(!filterOpen)}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: "100px",
                  }}
                >
                  Filters
                </StyledButton>
              </TransactionsFilterWrapper>
              <div>
                <StyledButton
                  variant="outlined"
                  startIcon={<CachedOutlined fontSize="small" />}
                  color="inherit"
                  onClick={handleRefresh}
                  sx={{ minWidth: "100px" }}
                >
                  <div>Refresh </div>
                </StyledButton>
                <div style={{ fontSize: "6px", margin: 2, fontWeight: "bold" }}>
                  Refreshed:
                  {dayjs(refreshTime).format("DD/MM/YYYY: hh:mm a")}
                </div>
              </div>
            </StyledBox>

            {rowSelectionModel.length &&
            allowedCategorisationRoles.includes(role) ? (
              <>
                <Divider />
                <TransactionCategorySelector
                  category={category}
                  fetchTransaction={fetchTransaction}
                />
              </>
            ) : (
              <></>
            )}

            <TransactionTable
              transactions={transactionList}
              isLoading={isLoading}
              pageInfo={pageInfo}
              paginationModel={pagination}
              setPaginationModel={setPaginationModel}
              sortModel={sortModel as GridSortItem[]}
              onSortModelChange={handleSortModelChange}
              fetchTransaction={fetchTransaction}
            />

            {uploadDialogOpen && (
              <UploadPopup
                open={uploadDialogOpen}
                onClose={handleSetUploadDialogClose}
                bankList={bankList}
              />
            )}
            {ScheduleDialogOpen && (
              <ScheduleReconciliationPopup
                open={ScheduleDialogOpen}
                onClose={handleSetScheduleDialogClose}
              />
            )}
          </Stack>
        </ContentWrapper>
        <StyledFilterDrawer
          variant={filterOpen ? "persistent" : "temporary"}
          anchor="right"
          open={filterOpen}
          sx={{
            flexShrink: 0,
          }}
        >
          <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <Button color="inherit" onClick={handleDrawerClose}>
                <CloseIcon />
              </Button>
            </Box>
            <TransactionsFilters
              resetFilterHandler={resetFilterHandler}
              ApplyFilterHandler={ApplyFilterHandler}
            />
          </Box>
        </StyledFilterDrawer>
      </TransactionsContainer>
    </GridParent>
  )
}
